/**
 * Hand-authored pixel sprites as ASCII maps: one character per pixel, `.` transparent, every other
 * character looked up in the sprite's palette. Pure data (no Phaser), validated in tests.
 */

export interface SpriteSheet {
  readonly width: number;
  readonly height: number;
  readonly palette: Readonly<Record<string, string>>;
  readonly frames: readonly (readonly string[])[];
}

// --- The player: Dad's suit, 16 × 24, facing right -------------------------------------------

const SUIT_TOP = [
  '................',
  '.....kkkkkk.....',
  '....kwwwwwwk....',
  '...kwwwwwwwwk...',
  '...kwwwkkkkkk...',
  '..jkwwkvvvvVk...',
  '..jkwwkvvvvvk...',
  '..jkwwwkkkkkk...',
  '.jjjkwwwwwwk....',
  '.jJjkkoooookk...',
  '.jJjkwwwwwwwk...',
  '.jJjkwwwswwwdk..',
  '.jJjkwwwsdddddk.',
  '.jjjkwwwskkkkkk.',
  '..jjkoooOoook...',
  '....kwwwswwk....',
  '....kwwsswwk....',
  '....kwwkkwwk....',
];

const LEGS_STAND = [
  '....kwsk.kwsk...',
  '....kwsk.kwsk...',
  '....kwsk.kwsk...',
  '...kddsk.kddsk..',
  '...kdddk.kdddk..',
  '...kkkkk.kkkkk..',
];

const LEGS_STRIDE = [
  '....kwskkwsk....',
  '...kwsk..kwsk...',
  '..kwsk....kwsk..',
  '.kddsk....kddsk.',
  '.kdddk.....kddk.',
  '.kkkkk.....kkkk.',
];

const LEGS_PASS = [
  '....kwsk.kwsk...',
  '....kwskkwsk....',
  '.....kwskwsk....',
  '....kddskddsk...',
  '....kdddkdddk...',
  '....kkkkkkkkk...',
];

const LEGS_AIR = [
  '....kwsk.kwsk...',
  '...kwsk..kwsk...',
  '..kddsk..kddsk..',
  '..kdddk..kdddk..',
  '..kkkkk..kkkkk..',
  '................',
];

/** Writes `overlay` rows onto `rows` starting at row `top`; `.` in the overlay leaves a pixel. */
function stamp(rows: string[], overlay: readonly string[], top: number): string[] {
  const out = [...rows];
  overlay.forEach((line, i) => {
    const y = top + i;
    const base = out[y];
    if (base === undefined) return;
    out[y] = [...base].map((ch, x) => (line[x] && line[x] !== '.' ? line[x] : ch)).join('');
  });
  return out;
}

const FLAME_A = ['.fff', '.fFf', '.FfF', '..F.', '..F.'];
const FLAME_B = ['.fff', '.FfF', '..f.', '..F.'];
const FLUTTER_PUFF = ['.J.J', 'J.J.'];

export const PLAYER_FRAME = {
  stand: 0,
  stride: 1,
  pass: 2,
  stride2: 3,
  air: 4,
  jetA: 5,
  jetB: 6,
  flutter: 7,
} as const;

export const PLAYER_SHEET: SpriteSheet = {
  width: 16,
  height: 24,
  palette: {
    k: '#120e1c',
    w: '#eef2f8',
    s: '#9aa6bd',
    d: '#5a6378',
    v: '#ffb43a',
    V: '#ffe7a8',
    o: '#ff8a24',
    O: '#c45a14',
    j: '#3a3f55',
    J: '#7b84a6',
    f: '#ffd23f',
    F: '#ff5a2a',
  },
  frames: [
    [...SUIT_TOP, ...LEGS_STAND],
    [...SUIT_TOP, ...LEGS_STRIDE],
    [...SUIT_TOP, ...LEGS_PASS],
    // The second stride is the first one mirrored at the legs only.
    [...SUIT_TOP, ...LEGS_STRIDE.map((r) => [...r].reverse().join(''))],
    [...SUIT_TOP, ...LEGS_AIR],
    stamp([...SUIT_TOP, ...LEGS_AIR], FLAME_A, 15),
    stamp([...SUIT_TOP, ...LEGS_AIR], FLAME_B, 15),
    stamp([...SUIT_TOP, ...LEGS_AIR], FLUTTER_PUFF, 15),
  ],
};

// --- Crawler: a beetle, 16 × 12, facing left ----------------------------------------------

const CRAWLER_BODY = [
  '................',
  '................',
  '.....kkkkkk.....',
  '...kkPPPppppkk..',
  '..kPPppppppppqk.',
  '.kPpppppppppqqk.',
  '.kGgppppppppqqk.',
  '.kggqqqqqqqqqqk.',
  '..kkkkkkkkkkkk..',
];

export const CRAWLER_SHEET: SpriteSheet = {
  width: 16,
  height: 12,
  palette: {
    k: '#120c18',
    p: '#7a3fa0',
    P: '#b46ad8',
    q: '#4a2366',
    g: '#7dffb0',
    G: '#e8fff0',
  },
  frames: [
    [...CRAWLER_BODY, '..k..k..k..k..k.', '.k..k..k..k..k..', '................'],
    [...CRAWLER_BODY, '...k..k..k..k..k', '..k..k..k..k..k.', '................'],
  ],
};

// --- Starlight cell, 10 × 10 -------------------------------------------------------------------

const CELL_A = [
  '....w.....',
  '...cYc....',
  '..cYyYc...',
  '.cYywyYc..',
  'wYywwwyYw.',
  '.cYywyYc..',
  '..cYyYc...',
  '...cYc....',
  '....w.....',
  '..........',
];

/** The twinkle: the four sparkle tips go out. */
const CELL_B = [
  '..........',
  '...cYc....',
  '..cYyYc...',
  '.cYywyYc..',
  '.YywwwyY..',
  '.cYywyYc..',
  '..cYyYc...',
  '...cYc....',
  '..........',
  '..........',
];

export const CELL_SHEET: SpriteSheet = {
  width: 10,
  height: 10,
  palette: { y: '#fff2a8', Y: '#ffd23f', c: '#7df9ff', w: '#ffffff' },
  frames: [CELL_A, CELL_B],
};

// --- Material shard: a rare violet crystal, 10 × 10 ---------------------------------------------

const SHARD_A = [
  '....k.....',
  '...kVk....',
  '..kVvVk...',
  '..kVvvk...',
  '.kVvWvvk..',
  '.kvvvvVk..',
  '..kvvVk...',
  '..kvVvk...',
  '...kvk....',
  '....k.....',
];

export const SHARD_SHEET: SpriteSheet = {
  width: 10,
  height: 10,
  palette: { k: '#1a0f2e', v: '#b46ad8', V: '#e8c8ff', W: '#ffffff' },
  // The glint moves to the tip.
  frames: [SHARD_A, SHARD_A.map((r, y) => (y === 4 ? '.kVvVvvk..' : y === 1 ? '...kWk....' : r))],
};

// --- Laser bolt, 8 × 3 -------------------------------------------------------------------------

export const LASER_SHEET: SpriteSheet = {
  width: 8,
  height: 3,
  palette: { o: '#ff8a24', y: '#ffe066', w: '#ffffff' },
  frames: [['.oyyyyo.', 'oywwwwyo', '.oyyyyo.']],
};

// --- Piercing beam, 12 × 3: a laser with the piercing mod, longer and violet --------------------

export const BEAM_SHEET: SpriteSheet = {
  width: 12,
  height: 3,
  palette: { p: '#b46ad8', P: '#e8a8ff', w: '#ffffff' },
  frames: [['.ppPPPPPPpp.', 'pPwwwwwwwwPp', '.ppPPPPPPpp.']],
};

// --- Weapon mod cache, 12 × 12: an armoured case, its core pulsing -------------------------------

const CACHE_A = [
  '..kkkkkkkk..',
  '.kssssssssk.',
  'kssSSSSSSssk',
  'ksSkkkkkkSsk',
  'ksSkcccckSsk',
  'ksSkcCCckSsk',
  'ksSkcCCckSsk',
  'ksSkcccckSsk',
  'ksSkkkkkkSsk',
  'kssSSSSSSssk',
  '.kssssssssk.',
  '..kkkkkkkk..',
];

export const CACHE_SHEET: SpriteSheet = {
  width: 12,
  height: 12,
  palette: { k: '#120e1c', s: '#46557e', S: '#8a9ac8', c: '#2f7f86', C: '#7df9ff' },
  frames: [CACHE_A, CACHE_A.map((r) => r.replace(/[cC]/g, (ch) => (ch === 'c' ? 'C' : 'c')))],
};

// --- Rocket, 8 × 5, pointing right (the renderer rotates it) ----------------------------------

const ROCKET_A = ['..kkkk..', 'fokwwwk.', 'Fokwwwwk', 'fokwwwk.', '..kkkk..'];

export const ROCKET_SHEET: SpriteSheet = {
  width: 8,
  height: 5,
  palette: { k: '#120e1c', w: '#eef2f8', o: '#ff8a24', f: '#ffd23f', F: '#ff5a2a' },
  // The exhaust flickers: swap the two flame colours.
  frames: [ROCKET_A, ROCKET_A.map((r) => r.replace(/[fF]/g, (c) => (c === 'f' ? 'F' : 'f')))],
};

// --- Scout drone: the captors' machine, 12 × 12 ------------------------------------------------

const DRONE_A = [
  '....kkkk....',
  '..kkmmmmkk..',
  '.kmmMMMMmmk.',
  '.kmMkkkkMmk.',
  'kmMkeeeekMmk',
  'kmMkeEEekMmk',
  'kmMkeEEekMmk',
  'kmMkeeeekMmk',
  '.kmMkkkkMmk.',
  '.kmmmmmmmmk.',
  '..kk.tt.kk..',
  '.....t......',
];

export const DRONE_FRAME = { idleA: 0, idleB: 1, aim: 2 } as const;

export const DRONE_SHEET: SpriteSheet = {
  width: 12,
  height: 12,
  palette: {
    k: '#0c0a16',
    m: '#2f7f86',
    M: '#7df9ff',
    e: '#a01a4a',
    E: '#ff3a7a',
    W: '#ffffff',
    t: '#7df9ff',
  },
  frames: [
    DRONE_A,
    // The thruster flickers.
    [...DRONE_A.slice(0, 10), '..kk.t.tkk..', '............'],
    // Aiming: the eye burns white-hot.
    DRONE_A.map((r) => r.replace(/E/g, 'W').replace(/e/g, 'E')),
  ],
};

// --- Hostile bolt, 6 × 6 -----------------------------------------------------------------------

export const BOLT_SHEET: SpriteSheet = {
  width: 6,
  height: 6,
  palette: { p: '#a01a4a', P: '#ff3a7a', W: '#ffffff' },
  frames: [
    ['..pp..', '.pPPp.', 'pPWWPp', 'pPWWPp', '.pPPp.', '..pp..'],
    ['......', '..PP..', '.PWWP.', '.PWWP.', '..PP..', '......'],
  ],
};

// --- Daze stars over a stunned boss, 14 × 5 ----------------------------------------------------

export const DAZE_SHEET: SpriteSheet = {
  width: 14,
  height: 5,
  palette: { y: '#ffd23f', w: '#ffffff' },
  frames: [
    ['.y.......w....', 'yyy.....www...', '.y.......w....', '.....y........', '....yyy.......'],
    ['....w.......y.', '...www.....yyy', '....w.......y.', '.........y....', '........yyy...'],
  ],
};

// --- The Ranger: a rogue Galactiv suit, the player's suit gone wrong ---------------------------

/** Same pixel map as the player (it is the same suit model), recoloured: rust, a red visor. */
export const RANGER_SHEET: SpriteSheet = {
  ...PLAYER_SHEET,
  palette: {
    ...PLAYER_SHEET.palette,
    w: '#b8553a',
    s: '#7a3324',
    d: '#4a1f1a',
    v: '#ff2a4a',
    V: '#ffb0b8',
    o: '#3a2a2a',
    O: '#1a1010',
    j: '#4a2424',
    J: '#8a4a3a',
  },
};

// --- Transit: the suit at light speed, 20 × 10, flying right ------------------------------------

const SHIP_A = [
  '....................',
  '...........jjjj.kkk.',
  '..........jJJJJkwwwk',
  '.ff.kkkkkkkkkkkwvvVk',
  'fFfkwwwwwwwwwwkwvvvk',
  'FfFkssswwwwwwwwkwwk.',
  'fFfkddsssssoooookkk.',
  '.ff.kkkkkkkkkkkkk...',
  '....................',
  '....................',
];

export const SHIP_SHEET: SpriteSheet = {
  width: 20,
  height: 10,
  palette: PLAYER_SHEET.palette,
  // The jet flickers: swap the two flame colours.
  frames: [SHIP_A, SHIP_A.map((r) => r.replace(/[fF]/g, (c) => (c === 'f' ? 'F' : 'f')))],
};

// --- Transit: raider saucer, the captors' patrol craft, 14 × 10 --------------------------------

const RAIDER_A = [
  '.....kkkk.....',
  '....kMMMMk....',
  '...kMMmmMMk...',
  '.kkkkkkkkkkkk.',
  'kmmEmmEmmEmmmk',
  'kmmmmmmmmmmmmk',
  '.kkkkkkkkkkkk.',
  '..t........t..',
  '..............',
  '..............',
];

export const RAIDER_SHEET: SpriteSheet = {
  width: 14,
  height: 10,
  palette: DRONE_SHEET.palette,
  // The running lights chase along the rim.
  frames: [RAIDER_A, RAIDER_A.map((r) => r.replace('kmmEmmEmmEmmmk', 'kmEmmEmmEmmEmk'))],
};

/** Every row the right width, every frame the right height, every character in the palette. */
export function sheetProblems(name: string, sheet: SpriteSheet): string[] {
  const problems: string[] = [];
  sheet.frames.forEach((frame, f) => {
    if (frame.length !== sheet.height) {
      problems.push(`${name} frame ${f}: ${frame.length} rows, not ${sheet.height}`);
    }
    frame.forEach((row, y) => {
      if (row.length !== sheet.width) {
        problems.push(`${name} frame ${f} row ${y}: ${row.length} wide, not ${sheet.width}`);
      }
      for (const ch of row) {
        if (ch !== '.' && !(ch in sheet.palette)) {
          problems.push(`${name} frame ${f} row ${y}: '${ch}' not in palette`);
        }
      }
    });
  });
  return problems;
}
